/* global globalThis */
import { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import WatchLaterRoundedIcon from "@mui/icons-material/WatchLaterRounded";
import CloseIcon from "@mui/icons-material/Close";
import { createHeaderWithSort } from "./columns.jsx";
import ProfileIcon from "../../../assets/manualTaskProfile.svg";
import { capitalizeFirstLetter } from "../../../utils/capitalize.js";

export const checkboxColumn = {
  id: "selection",
  header: ({ table }) => {
    const allSelected = table.getIsAllPageRowsSelected();
    const someSelected = table.getIsSomePageRowsSelected();

    let selectAllAriaLabel = "Select all rows";
    if (allSelected) {
      selectAllAriaLabel = "Select all rows (all selected)";
    } else if (someSelected) {
      selectAllAriaLabel = "Select all rows (partially selected)";
    }
    return (
      <>
        <Checkbox
          id="manual-tasks-select-all"
          checked={table.getIsAllPageRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
          indeterminate={table.getIsSomePageRowsSelected()}
          slotProps={{
            input: {
              id: "manual-tasks-select-all-input",
              "aria-labelledby": "manual-tasks-select-all-label",
              "aria-label": selectAllAriaLabel,
            },
          }}
          sx={{ mr: 0.5 }}
        />
        <span
          id="manual-tasks-select-all-label"
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
          }}
        >
          Select all rows
        </span>
      </>
    );
  },
  cell: ({ row }) => {
    const labelText = `Select row for ${
      row.original?.recipient?.name || row.original?.recipientName || "item"
    }`;
    const id = `manual-tasks-row-${row.id}-checkbox`;
    return (
      <>
        <Checkbox
          id={id}
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          onClick={(e) => e.stopPropagation()}
          slotProps={{
            input: {
              id: `${id}-input`,
              "aria-labelledby": `${id}-label`,
              "aria-label":
                labelText + (row.getIsSelected() ? " (selected)" : ""),
            },
          }}
          sx={{
            "&.Mui-checked": {
              color: "#403C6F",
              "& .MuiSvgIcon-root": {
                fill: "#403C6F",
              },
            },
          }}
        />
        <span
          id={`${id}-label`}
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
          }}
        >
          {labelText}
        </span>
      </>
    );
  },
  size: 50,
  enableSorting: true,
};

const getRecipientUrl = (recipient) => {
  if (!recipient?.id) return null;

  const type = (recipient.type || "member").toLowerCase();

  return type === "member" ? `/members/${recipient.id}` : null;
};

const formatRecipientType = (type) => {
  if (!type) return "";
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return `${date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })} ${date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;
};

const RecipientCell = ({ row }) => {
  const navigate = useNavigate();
  const recipient = row.original.recipient || {};
  const recipientName = recipient.name || "N/A";
  const recipientType = recipient.type || "";
  const language = recipient.language || "";
  const timezone = recipient.timezone || "";

  const recipientUrl = getRecipientUrl(recipient);

  const formattedFullName = capitalizeFirstLetter(recipientName);
  const truncatedName =
    formattedFullName.length > 20
      ? formattedFullName.slice(0, 20) + "..."
      : formattedFullName;

  return (
    <div style={{ minWidth: "120px" }}>
      <Tooltip
        title={capitalizeFirstLetter(recipientName)}
        placement="bottom-start"
        arrow
        slotProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#343435",
                color: "#FFFFFF",
                margin: 0,
              },
              "& .MuiTooltip-arrow": {
                color: "#343435",
                margin: 0,
                left: "50% !important",
                transform: "translateX(-50%) !important",
              },
            },
          },
          tooltip: {
            sx: {
              margin: 0,
              marginTop: "0px !important",
              padding: "4px 8px",
            },
          },
        }}
      >
        <div
          style={{
            fontWeight: 600,
            color: "#403C6F",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "120px",
            display: "inline-block",
            verticalAlign: "middle",
            fontSize: "17px",
          }}
        >
          {recipientUrl ? (
            <a
              href={recipientUrl}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                try {
                  localStorage.setItem(
                    "nav_activeItem",
                    JSON.stringify("Member Outreach"),
                  );
                  localStorage.setItem("nav_activeSubItem", JSON.stringify(""));
                  localStorage.setItem("nav_openSubmenus", JSON.stringify({}));
                } catch (e) {
                  return `fetchPriorityOutreach failed: ${e.message}`;
                }

                globalThis.dispatchEvent(new Event("updateNavFromManualTasks"));
                navigate(recipientUrl, {
                  state: { fromManualTasks: true, source: "manual-tasks" },
                });
              }}
              style={{
                color: "#403C6F",
                textDecoration: "underline",
                display: "inline-block",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "middle",
              }}
              title={formattedFullName}
            >
              {truncatedName}
            </a>
          ) : (
            <span
              style={{
                display: "inline-block",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "middle",
              }}
            >
              {truncatedName}
            </span>
          )}
        </div>
      </Tooltip>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          marginTop: "4px",
        }}
      >
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <LanguageRoundedIcon
              style={{ fontSize: "14px", color: "#403C6F" }}
            />
            <span style={{ fontSize: "12px", color: "#403C6F" }}>
              {language || "N/A"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <WatchLaterRoundedIcon
              style={{ fontSize: "14px", color: "#403C6F" }}
            />
            <span style={{ fontSize: "12px", color: "#403C6F" }}>
              {timezone || "N/A"}
            </span>
          </div>
        </div>
        <div
          style={{
            fontSize: "12px",
            color: "#403C6F",
            marginTop: "2px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <img
            src={ProfileIcon}
            alt=""
            style={{
              width: "12px",
              height: "12px",
            }}
          />
          {formatRecipientType(recipientType)}
        </div>
      </div>
    </div>
  );
};

RecipientCell.propTypes = {
  row: PropTypes.object,
};

export const recipientColumn = {
  header: createHeaderWithSort("Recipient", true),
  accessorKey: "recipient.name",
  id: "recipient",
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.recipient?.name || "";
    const b = rowB.original.recipient?.name || "";
    return a.localeCompare(b);
  },
  size: 140,
  minSize: 120,
  maxSize: 160,
  cell: ({ row }) => <RecipientCell row={row} />,
  enableSorting: true,
};

export const dueDateColumn = {
  header: createHeaderWithSort("Due Date", true),
  accessorKey: "recentActivity",
  enableSorting: true,
  sortDescFirst: true,
  id: "dueDate",
  size: 150,
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.recentActivity?.dueDate;
    const b = rowB.original.recentActivity?.dueDate;
    if (!a) return 1;
    if (!b) return -1;
    return new Date(a) - new Date(b);
  },
  cell: ({ row }) => {
    const dueDate = row.original.dueDate
      ? new Date(row.original.dueDate)
      : null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isPastDue = dueDate && dueDate < today;

    return (
      <div
        style={{
          color: isPastDue ? "#D32F2F" : "#1B2F3F",
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: "150%",
          letterSpacing: "0%",
        }}
      >
        {dueDate
          ? dueDate.toLocaleDateString("en-US", {
              month: "numeric",
              day: "numeric",
              year: "numeric",
            })
          : "-"}
      </div>
    );
  },
};

export const outreachReasonColumn = {
  header: createHeaderWithSort("Outreach Reason", true),
  accessorKey: "outreachReason",
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.outreachReason || "";
    const b = rowB.original.outreachReason || "";
    return a.localeCompare(b);
  },
  cell: ({ row }) => {
    const reasonRaw = row.original.outreachReason ?? "";
    return (
      <Tooltip
        title={reasonRaw}
        placement="bottom-start"
        arrow
        slotProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#343435",
                color: "#FFFFFF",
                margin: 0,
                fontSize: "12px",
                fontWeight: 500,
              },
              "& .MuiTooltip-arrow": {
                color: "#343435",
                margin: 0,
                left: "50% !important",
                transform: "translateX(-50%) !important",
              },
            },
          },
          tooltip: { sx: { margin: "0 !important", padding: "4px 8px" } },
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 12px",
            borderRadius: "20px",
            backgroundColor: "#ECECF1",
            color: "#403C6F",
            fontSize: "14px",
            fontWeight: 500,
            height: "24px",
            lineHeight: "16px",
            maxWidth: "240px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          aria-label={`Outreach reason: ${reasonRaw}`}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {reasonRaw}
          </span>
        </div>
      </Tooltip>
    );
  },
  size: 200,
  enableSorting: true,
};

export const attemptsColumn = {
  header: createHeaderWithSort(
    <Tooltip title="No. of Attempts" arrow>
      <span>No. of Attempts</span>
    </Tooltip>,
    true,
  ),
  accessorKey: "attempts",
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.attempts || 0;
    const b = rowB.original.attempts || 0;
    return a - b;
  },
  cell: ({ row }) => (
    <div
      style={{
        textAlign: "left",
        fontSize: "16px",
        whiteSpace: "nowrap",
        color: "#403C6F",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",
        display: "inline-block",
      }}
    >
      {row.original.attempts}
    </div>
  ),

  size: 60,
  minSize: 60,
  maxSize: 80,
  enableSorting: true,
};

const TaskNotePopup = ({ open, onClose, note, title = "Task Note" }) => {
  const dialogTitleId = "task-note-dialog-title";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby={dialogTitleId}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "8px",
            maxWidth: "600px",
            backgroundColor: "#F7F8FB",
          },
        },
      }}
    >
      <DialogTitle
        id={dialogTitleId}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, fontSize: "18px", color: "#1F2937" }}
        >
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close task note dialog"
          sx={{
            color: "#6B7280",
            "&:hover": {
              backgroundColor: "rgba(107, 114, 128, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          border: "1px solid #E9E9E8",
          backgroundColor: "#FFFFFF",
          marginLeft: "22px",
          marginRight: "22px",
          borderRadius: "8px",
          marginBottom: "20px",
          paddingBottom: "16px",
          paddingTop: "16px !important",
        }}
      >
        <Typography
          sx={{
            color: "#403C6F",
            fontSize: "16px",
            lineHeight: "1.6",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {note || "No note available"}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

TaskNotePopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  note: PropTypes.string,
  title: PropTypes.string,
};

const TaskNoteCell = ({ note, recipientName }) => {
  const [showPopup, setShowPopup] = useState(false);

  const maxLength = 100;
  const needsTruncation = note?.length > maxLength;
  const displayText = needsTruncation
    ? note.substring(0, maxLength) + "..."
    : note;

  const handleReadMore = (e) => {
    e.stopPropagation();
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const popupTitle = recipientName
    ? `Task Note (${recipientName})`
    : "Task Note";

  return (
    <div style={{ width: "100%", minWidth: "200px" }}>
      <div
        style={{
          color: "#403C6F",
          lineHeight: "1.5",
          fontSize: "16px",
          display: "-webkit-box",
          WebkitLineClamp: needsTruncation ? 2 : "none",
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordWrap: "break-word",
          hyphens: "auto",
          whiteSpace: "pre-line",
          maxHeight: needsTruncation ? "2.8em" : "none",
          width: "100%",
        }}
      >
        {displayText}
      </div>
      {needsTruncation && (
        <Button
          onClick={handleReadMore}
          sx={{
            padding: "2px 0",
            minWidth: "auto",
            textTransform: "none",
            fontSize: "12px",
            color: "#403C6F",
            textDecoration: "underline",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
              opacity: 0.8,
            },
            marginTop: "4px",
          }}
        >
          Read more
        </Button>
      )}
      <TaskNotePopup
        open={showPopup}
        onClose={handleClosePopup}
        note={note}
        title={popupTitle}
      />
    </div>
  );
};

TaskNoteCell.propTypes = {
  note: PropTypes.string,
  recipientName: PropTypes.string,
};

export const taskNoteColumn = {
  header: createHeaderWithSort("Task Note", true),
  accessorKey: "taskNote",
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.taskNote || "";
    const b = rowB.original.taskNote || "";
    return a.localeCompare(b);
  },
  cell: ({ row }) => {
    const recipientName = row.original.recipient?.name || "Unknown";
    return (
      <TaskNoteCell
        note={row.original.taskNote}
        recipientName={recipientName}
      />
    );
  },

  size: 1200,
  minSize: 1200,
  maxSize: 1200,
  enableSorting: true,
};

export const recentActivityColumn = {
  header: createHeaderWithSort("Recent Activity", true),
  accessorKey: "recentActivity",
  enableSorting: true,
  sortDescFirst: true,
  size: 240,
  sortingFn: (rowA, rowB) => {
    const a = rowA.original.recentActivity?.timestamp;
    const b = rowB.original.recentActivity?.timestamp;
    if (!a) return 1;
    if (!b) return -1;
    return new Date(a) - new Date(b);
  },
  id: "recentActivity",
  cell: ({ row }) => {
    const activity = row.original.recentActivity || {};
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          whiteSpace: "nowrap",
        }}
      >
        {activity.status && (
          <div
            style={{
              color: activity.statusColor || activity.colourCode || "#6c757d",
              fontWeight: 600,
              fontSize: "14px",
              whiteSpace: "nowrap",
            }}
          >
            {activity.status}
          </div>
        )}
        {activity.timestamp && (
          <div
            style={{
              color: "#1B2F3F",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "150%",
              letterSpacing: "0%",
            }}
          >
            {formatDateTime(activity.timestamp)}
          </div>
        )}
      </div>
    );
  },
};

export const actionsColumn = (
  onQuickView,
  onCompleteTask,
  onEditTask,
  { canWrite = true } = {},
) => {
  const ActionsMenu = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const menuId = `task-actions-menu-${row.id}`;

    const recipientType = row.original.recipient?.type || "";
    const isMemberOrOthers =
      recipientType === "MEMBER" || recipientType === "OTHERS";
    const completeButtonText = isMemberOrOthers
      ? "Add Disposition"
      : "Complete Task";

    const handleClick = (event) => {
      event.stopPropagation();
      if (!canWrite) return;
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleEdit = (event) => {
      event.stopPropagation();
      if (!canWrite) return;
      if (onEditTask) {
        onEditTask(row.original);
      }
      handleClose();
    };

    const handleComplete = (event) => {
      event.stopPropagation();
      if (!canWrite) return;
      if (onCompleteTask) {
        onCompleteTask(row.original);
      }
      handleClose();
    };

    return (
      <div>
        <IconButton
          size="small"
          onClick={handleClick}
          aria-label="more"
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          disabled={!canWrite}
          sx={{
            opacity: canWrite ? 1 : 0.4,
            cursor: canWrite ? "pointer" : "not-allowed",
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          id={menuId}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: "#6E6D92",
                borderRadius: "6px",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                minWidth: "140px",
                "& .MuiList-root": {
                  padding: 0,
                },
              },
            },
            menu: {
              sx: {
                padding: 0,
              },
            },
          }}
        >
          <MenuItem
            onClick={handleEdit}
            disabled={!canWrite}
            aria-label={`Edit task for ${
              row.original.recipient?.name || "recipient"
            }`}
            sx={{
              backgroundColor: "#6E6D92",
              color: canWrite ? "white" : "#CCCCCC",
              "& .MuiListItemText-primary": {
                fontSize: "0.875rem",
                textShadow: canWrite ? "0 0 1px rgba(0, 0, 0, 0.3)" : "none",
              },
              padding: "8px 16px",
              opacity: canWrite ? 1 : 0.5,
              cursor: canWrite ? "pointer" : "not-allowed",
            }}
          >
            <ListItemText>Edit Task</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleComplete}
            disabled={!canWrite}
            aria-label={`${completeButtonText} for ${
              row.original.recipient?.name || "recipient"
            }`}
            sx={{
              backgroundColor: "#6E6D92",
              color: canWrite ? "white" : "#CCCCCC",
              "& .MuiListItemText-primary": {
                fontSize: "0.875rem",
                textShadow: canWrite ? "0 0 1px rgba(0, 0, 0, 0.3)" : "none",
              },
              padding: "8px 16px",
              opacity: canWrite ? 1 : 0.5,
              cursor: canWrite ? "pointer" : "not-allowed",
            }}
          >
            <ListItemText>{completeButtonText}</ListItemText>
          </MenuItem>
        </Menu>
      </div>
    );
  };

  ActionsMenu.propTypes = {
    row: PropTypes.object,
  };

  return {
    id: "actions",
    header: "",
    cell: ({ row }) => <ActionsMenu row={row} />,
    size: 60,
    enableSorting: true,
  };
};

export const getManualTasksColumns = (
  onQuickView,
  onCompleteTask,
  onEditTask,
  { canWrite = true } = {},
) => [
  checkboxColumn,
  recipientColumn,
  dueDateColumn,
  outreachReasonColumn,
  attemptsColumn,
  taskNoteColumn,
  recentActivityColumn,

  actionsColumn(
    onQuickView,
    (row) => {
      if (!canWrite) return;
      onCompleteTask(row);
    },
    (row) => {
      if (!canWrite) return;
      onEditTask(row);
    },
    { canWrite },
  ),
];

export default getManualTasksColumns;
